import { FC } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle } from '@fortawesome/free-solid-svg-icons';
import { remCalc } from '@lwtears/lwt-common-frontend/lib/@common/util';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { ButtonGroup, Button } from '@mui/material';
import { FONT_GSS_FUTURA_SEMI_BOLD } from '../../themes/defaultTheme';

export type ToggleItem = {
  value: string | number;
  text: string;
};

export enum ToggleVariant {
  'primary',
  'secondary',
}

type ToggleProps = {
  items: ToggleItem[];
  selectedValue: string | number;
  ariaLabel: string;
  onChange: (value: any) => void;
  variant?: ToggleVariant;
};

const ToggleButtonGroup: FC<ToggleProps> = ({
  items,
  selectedValue,
  ariaLabel,
  onChange,
  variant = ToggleVariant.primary,
}) => {
  const classes = useStyles(variant)();

  const defaultButtonStyle =
    variant === ToggleVariant.primary ? classes.defaultPrimary : classes.selectedPrimary;
  const selectedButtonStyle =
    variant === ToggleVariant.primary ? classes.selectedPrimary : classes.defaultPrimary;

  return (
    <ButtonGroup aria-label={ariaLabel} className={classes.root}>
      {items.map((item, index) => (
        <Button
          key={index}
          className={`${classes.button} ${
            selectedValue === item.value
              ? `${classes.selected} ${selectedButtonStyle}`
              : defaultButtonStyle
          }`}
          onClick={() => onChange(item.value)}
          aria-label={item.text}
          startIcon={
            selectedValue === item.value && (
              <FontAwesomeIcon icon={faCircle} className={classes.icon} />
            )
          }
        >
          {item.text}
        </Button>
      ))}
    </ButtonGroup>
  );
};

const useStyles = (variant: ToggleVariant) =>
  makeStyles((theme: Theme) => ({
    root: {
      height: remCalc(36),
      fontFamily: FONT_GSS_FUTURA_SEMI_BOLD,
    },
    button: {
      border: `2px solid ${
        variant === ToggleVariant.primary ? theme.palette.secondary.dark : 'white'
      }`,
      textTransform: 'none',
      fontFamily: FONT_GSS_FUTURA_SEMI_BOLD,
      fontSize: '14px',
      padding: '5px 31px',
      minWidth: remCalc(142),
    },
    selected: {
      textDecoration: 'underline',
    },
    selectedPrimary: {
      backgroundColor: theme.palette.secondary.dark,
      color: 'white',
      '&:hover': {
        backgroundColor: '#2f7dca',
      },
    },
    defaultPrimary: {
      backgroundColor: 'white',
      color: theme.palette.secondary.dark,
      '&:hover': {
        backgroundColor: '#cddef2',
      },
    },
    icon: {
      fontSize: '8px !important',
    },
  }));

export default ToggleButtonGroup;
